import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import PersonAddRounded from "@mui/icons-material/PersonAddRounded";
import BadgeRounded from "@mui/icons-material/BadgeRounded";
import EmailRounded from "@mui/icons-material/EmailRounded";
import CakeRounded from "@mui/icons-material/CakeRounded";
import PhoneRounded from "@mui/icons-material/PhoneRounded";
import CameraAltRounded from "@mui/icons-material/CameraAltRounded";
import StickyNote2Rounded from "@mui/icons-material/StickyNote2Rounded";
import User from "../../../api/User";
import TextField from "../../TextField";
import BirthdayField from "../../BirthdayField";
import BottomButton from "../../BottomButton";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../../constants/styles";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const UserRegistration = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [email, setEmail] = useState("");
    const [birth, setBirth] = useState("");
    const [phone, setPhone] = useState("");
    const [instagram, setInstagram] = useState("");
    const [note, setNote] = useState("");

    const [loading, setLoading] = useState(false);

    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 60);
    }, []);

    useEffect(() => {
        if (!uuid || !brand_id) {
            navigate("/");
            return;
        }
    }, [uuid, brand_id, navigate]);

    const handleBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/users");
        }
    }, [navigate]);

    const signUp = useCallback(() => {
        if (!uuid || !brand_id) return;
        if (name.trim() === "") {
            alert("Name is required.");
            return;
        }

        const isValidBirthday = birth.match(/^\d{2}\.\d{2}$/);
        if (birth && !isValidBirthday) {
            alert("Please enter a valid birthday before registering.");
            return;
        }

        setLoading(true);

        const convertBirthdayToDate = (
            birthday: string
        ): string | undefined => {
            const [day, month] = birthday.split(".");
            if (day && month) {
                return `1970-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
            }
            return undefined;
        };
        const formattedBirthday = birth
            ? convertBirthdayToDate(birth)
            : undefined;

        User.register(
            uuid,
            brand_id,
            email.trim(),
            `${name.trim()} ${surname.trim()}`,
            phone,
            instagram,
            "Ordinary",
            note,
            formattedBirthday
        )
            .then((res) => {
                if (!res) {
                    alert("This email is already used.");
                    return;
                }
                navigate(`/users/link/${res}`);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [
        uuid,
        brand_id,
        email,
        name,
        surname,
        birth,
        instagram,
        phone,
        note,
        navigate,
    ]);

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    return (
        <Stack
            ref={scrollRef}
            onScroll={handleScroll}
            flex={1}
            sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
        >
            {/* ── Hero ── */}
            <Stack
                ref={heroRef}
                alignItems="center"
                justifyContent="center"
                sx={{
                    width: "100%",
                    minHeight: 120,
                    position: "relative",
                    zIndex: 0,
                    background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                    transition: "background 0.3s ease",
                    pt: "40px",
                    pb: 4,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        opacity: 0.04,
                        backgroundImage: NOISE_TEXTURE,
                        backgroundRepeat: "repeat",
                        backgroundSize: "128px 128px",
                        pointerEvents: "none",
                    },
                }}
            >
                <ButtonBase
                    onClick={handleBack}
                    sx={{
                        position: "absolute", top: 12, left: 10, zIndex: 2,
                        width: 36, height: 36, borderRadius: "50%",
                        bgcolor: "rgba(60,60,60,0.35)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                >
                    <ColoredIcon icon={BackIcon} color="white" size="28px" />
                </ButtonBase>

                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: "rgba(60,60,60,0.3)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                    }}
                >
                    <PersonAddRounded sx={{ fontSize: 32, color: "white" }} />
                </Box>

                <Box
                    sx={{
                        bgcolor: "rgba(60,60,60,0.35)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: "16px",
                        px: 1.5,
                        py: 0.5,
                    }}
                >
                    <Typography fontSize="12px" fontWeight={500} color="white">
                        New Client
                    </Typography>
                </Box>
            </Stack>

            {/* ── Sticky Toolbar ── */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 4,
                    bgcolor: "white",
                    borderTopLeftRadius: "24px",
                    borderTopRightRadius: "24px",
                    mt: "-24px",
                    pt: 1.5,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        maxHeight: scrolledPastHero ? 52 : 0,
                        opacity: scrolledPastHero ? 1 : 0,
                        overflow: "hidden",
                        transition: "max-height 0.25s ease, opacity 0.2s ease",
                        px: 1,
                        borderBottom: scrolledPastHero
                            ? "1px solid rgba(0,0,0,0.06)"
                            : "none",
                    }}
                >
                    <ButtonBase onClick={handleBack} sx={{ p: 0.5 }}>
                        <ColoredIcon icon={BackIcon} color={primary} size="28px" />
                    </ButtonBase>
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="16px"
                        fontWeight={600}
                        ml={0.5}
                        noWrap
                        sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        New Client
                    </Typography>
                </Stack>
            </Box>

            {/* ── Content ── */}
            <Stack
                sx={{
                    position: "relative",
                    zIndex: 1,
                    bgcolor: "white",
                    boxSizing: "border-box",
                    px: 2,
                    pt: 0.5,
                    pb: "calc(4vh + 90px)",
                    "@keyframes fadeSlideIn": {
                        from: { opacity: 0, transform: "translateY(12px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeSlideIn 0.3s ease-out",
                }}
                spacing={2}
            >
                {/* ── Reassurance blurb ── */}
                <Typography
                    fontSize="13px"
                    lineHeight="19px"
                    color="text.secondary"
                >
                    Only a name is needed to get started. Everything else can
                    be added later.
                </Typography>

                {/* ── NAME section ── */}
                <Typography
                    fontSize="11px"
                    fontWeight={600}
                    color="text.secondary"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                    mb={-1}
                >
                    Basic Info
                </Typography>
                <Stack sx={CARD_STYLES} spacing={2.5}>
                    {/* Name */}
                    <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={iconBoxSx}>
                                <BadgeRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>Name</Typography>
                        </Stack>
                        <TextField
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            placeholder="First name"
                            variant="underlined"
                        />
                    </Stack>
                </Stack>

                {/* ── ADDITIONAL DETAILS section ── */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={-1}>
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                    >
                        Additional Details
                    </Typography>
                    <Typography
                        fontSize="11px"
                        fontWeight={500}
                        color="text.disabled"
                        fontStyle="italic"
                    >
                        Optional
                    </Typography>
                </Stack>
                <Stack sx={CARD_STYLES} spacing={2.5}>
                    {/* Surname */}
                    <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={iconBoxSx}>
                                <BadgeRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>Surname</Typography>
                        </Stack>
                        <TextField
                            value={surname}
                            onChange={(e) => setSurname(e.currentTarget.value)}
                            placeholder="Last name"
                            variant="underlined"
                        />
                    </Stack>

                    {/* E-Mail */}
                    <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={iconBoxSx}>
                                <EmailRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>E-Mail</Typography>
                        </Stack>
                        <TextField
                            value={email}
                            onChange={(e) => setEmail(e.currentTarget.value)}
                            variant="underlined"
                            type="email"
                        />
                    </Stack>

                    {/* Birthday */}
                    <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={iconBoxSx}>
                                <CakeRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>Birthday</Typography>
                        </Stack>
                        <BirthdayField
                            value={birth || ""}
                            onChange={(newVal: string) => setBirth(newVal)}
                        />
                    </Stack>

                    {/* Phone */}
                    <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={iconBoxSx}>
                                <PhoneRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>Phone</Typography>
                        </Stack>
                        <TextField
                            value={phone}
                            onChange={(e) => setPhone(e.currentTarget.value)}
                            variant="underlined"
                            type="tel"
                        />
                    </Stack>

                    {/* Instagram */}
                    <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={iconBoxSx}>
                                <CameraAltRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>Instagram</Typography>
                        </Stack>
                        <TextField
                            value={instagram}
                            onChange={(e) => setInstagram(e.currentTarget.value)}
                            variant="underlined"
                        />
                    </Stack>
                </Stack>

                {/* ── NOTE section ── */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={-1}>
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                    >
                        Note
                    </Typography>
                    <Typography
                        fontSize="11px"
                        fontWeight={500}
                        color="text.disabled"
                        fontStyle="italic"
                    >
                        Optional
                    </Typography>
                </Stack>
                <Stack sx={CARD_STYLES} spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={iconBoxSx}>
                            <StickyNote2Rounded
                                sx={{ fontSize: 18, color: primary }}
                            />
                        </Box>
                        <Typography sx={FORM_LABEL_SX}>Client note</Typography>
                    </Stack>
                    <TextField
                        value={note}
                        onChange={(e) => setNote(e.currentTarget.value)}
                        placeholder="Add a note about this client..."
                        fullWidth
                        multiline
                        variant="contained"
                        sx={{
                            bgcolor: "transparent",
                            fontWeight: 400,
                            fontSize: "15px",
                        }}
                    />
                </Stack>

                {/* ── Register button ── */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <BottomButton
                        disabled={loading || !name.trim()}
                        onClick={() => signUp()}
                    >
                        Register
                    </BottomButton>
                </Box>
            </Stack>
        </Stack>
    );
};

export default UserRegistration;
